<template>
  <div class="md:flex md:gap-4">
    <div v-show="!isLoRaWanGateway" class="md:basis-1/2">
      <BaseFormInput
        v-model="localDevice.serialNumber"
        label="Seriennummer"
        :validation="validation.serialNumber"
        :type="serialNumberType"
        test-id="serialNumber"
        :warning="isSerialNumberDuplicated"
        :warning-message="i18n.warning.duplicatedSerialNumber"
        :uppercase="isSmartRadiatorThermostat"
      >
        <template #trailing>
          <span
            class="mdi mdi-qrcode-scan text-3xl"
            @click="openDeviceCodeScanner"
          />
        </template>
      </BaseFormInput>
    </div>
    <div :class="'md:basis-1/2'">
      <BaseFormInput
        v-if="!isLegacyDevice"
        v-model="localDevice.customizedNumber"
        :validation="validation.customizedNumber"
        label="Artikelnummer"
        class="md:basis-1/2"
        :type="
          isSmartRadiatorThermostat || isLoRaWanGateway ? 'number' : 'text'
        "
        test-id="customizedNumber"
      />
    </div>
    <div v-show="isLoRaWanGateway" class="md:basis-1/2">
      <BaseFormInput
        v-model="localDevice.eui"
        label="EUI"
        :validation="validation.eui"
        type="text"
        test-id="eui"
      >
        <template v-if="isLoRaWanGateway" #trailing>
          <span
            class="mdi mdi-qrcode-scan text-3xl"
            @click="openDeviceCodeScanner"
          />
        </template>
      </BaseFormInput>
    </div>
  </div>

  <div class="md:flex md:gap-4">
    <BaseFormInput
      v-if="!isSmartRadiatorThermostat && !isLoRaWanGateway"
      v-model="localDevice.productKey"
      :validation="validation.productKey"
      label="Hersteller Artikelnummer"
      :class="[' md:basis-1/2', { 'pr-2': isDmgw || isHca || isSmokeDetector }]"
      placeholder="Hersteller Artikelnummer"
      type="text"
      test-id="articleNumber"
    />
    <BaseFormInput
      v-if="isDeviceMeteringDevice"
      v-model="localDevice.yearOfConformity"
      :validation="validation.yearOfConformity"
      class="md:basis-1/2"
      label="Eichjahr"
      type="number"
      inputmode="numeric"
      pattern="[0-9]{4}"
      placeholder="Eichjahr"
      test-id="yearOfConformity"
    />
  </div>
  <div v-show="false">
    <BaseFormInput
      v-model="localDevice.unknownProperties"
      label="Ungenaue Scandaten"
      :disabled="true"
      type="hidden"
    />
  </div>

  <div
    v-if="
      isSmartRadiatorThermostat &&
      !useOrderStore().isOnSiteInspection() &&
      showCounterPressure
    "
  >
    <BaseInformation
      class="-mb-px mt-4"
      status="warning"
      information-text="Bitte messen Sie den Ventilgegendruck erst nachdem alle Adapter installiert wurden."
    />
    <div class="flex gap-4">
      <BaseFormInput
        v-model.number="localDevice.counterPressure"
        :validation="validation.counterPressure"
        label="Ventilgegendruck"
        inputmode="decimal"
        type="number"
        test-id="counterPressure"
        class="grow"
      />
      <MeasurementUnit
        :device="localDevice"
        :unit="localDevice.measurementUnit"
      />
    </div>
  </div>
  <div v-if="isCodeScanActive">
    <DeviceCodeScanner
      :device-type="device.deviceType"
      @scan-result="scanResult($event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, WritableComputedRef } from 'vue';
import { detectionTypes, deviceTypes } from '@/enums/device';
import { DeviceClasses, LoRaWanGateway } from '@/models/devices/Device';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import { Validation } from '@vuelidate/core';
import DeviceCodeScanner from '@/components/UI/Scanner/DeviceCodeScanner.vue';
import MeasurementUnit from '@/components/Forms/DeviceFormComponents/MeasurementUnit.vue';
import { useDeviceStore } from '@/store/entities/deviceStore';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import { unknownPropertiesHelper } from '@/utils/scanner/unknownPropertiesHelper';
import { useOrderStore } from '@/store/order/orderStore';
import i18n from '@/utils/i18n/form.json';
import {
  isLoRaWanGatewayDevice,
  isSmokeDetectorDevice,
  isSmartRadiatorThermostatDevice,
  isDirectMeterGatewayDevice,
  isHeatCostAllocatorDevice,
  isLegacyMeteringDevice,
} from '@/components/Forms/Helper/deviceTypeHelper';
import articleNumberMapper from '@/models/scanner/ArticleNumberMapper';
import useHardwareScanListener from '@/components/UI/Scanner/useHardwareScanListener';

const props = defineProps({
  device: { type: Object, required: true },
  validation: { type: Object, required: true },
  showCounterPressure: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:device']);

const localDevice: WritableComputedRef<DeviceClasses> = computed({
  get() {
    return props.device;
  },
  set(value) {
    emit('update:device', value);
  },
});

const isCodeScanActive = ref(false);

const serialNumberType = computed(() => {
  if (
    localDevice.value.deviceType === deviceTypes.SmartRadiatorThermostat ||
    localDevice.value.deviceType === deviceTypes.LoRaWanGateway
  ) {
    return 'text';
  }
  return 'number';
});

const openDeviceCodeScanner = () => {
  return (isCodeScanActive.value = true);
};

const scanResult = (scanDataObject: Record<string, unknown> | undefined) => {
  if (!scanDataObject || Object.keys(scanDataObject).length === 0) {
    isCodeScanActive.value = false;
    localDevice.value.detectionType = detectionTypes.ManuelleErfassung;
    return;
  }
  if (scanDataObject.unknownProperties) {
    const currentUnknownProperties = localDevice.value.unknownProperties;
    localDevice.value.unknownProperties = unknownPropertiesHelper(
      currentUnknownProperties,
      scanDataObject.unknownProperties as string
    );
    localDevice.value.detectionType = detectionTypes.CodeScannen;
  }

  Object.entries(scanDataObject).forEach(([key, value]) => {
    if (key !== 'unknownProperties' && key in localDevice.value) {
      localDevice.value[key] = value;
    }
  });
  isCodeScanActive.value = false;
};

const isDeviceMeteringDevice = computed(() => {
  if (localDevice.value?.isMeteringDevice) {
    return (
      localDevice.value?.isMeteringDevice() &&
      localDevice.value.deviceType !== deviceTypes.HeatCostAllocator
    );
  }
  return false;
});

const isLoRaWanGateway = computed(() => {
  return isLoRaWanGatewayDevice(localDevice.value.deviceType);
});

const isSmartRadiatorThermostat = computed(() => {
  return isSmartRadiatorThermostatDevice(localDevice.value.deviceType);
});

const isSmokeDetector = computed(() => {
  return isSmokeDetectorDevice(localDevice.value.deviceType);
});

const isDmgw = computed(() => {
  return isDirectMeterGatewayDevice(localDevice.value.deviceType);
});

const isHca = computed(() => {
  return isHeatCostAllocatorDevice(localDevice.value.deviceType);
});

const isLegacyDevice = computed(() => {
  return isLegacyMeteringDevice(localDevice.value.deviceType);
});

const isSerialNumberDuplicated = computed(() => {
  return useDeviceStore().isDeviceDuplicated(localDevice.value);
});

watch(
  () =>
    localDevice.value instanceof LoRaWanGateway
      ? localDevice.value.eui
      : localDevice.value.serialNumber,
  () => {
    if (localDevice.value.unknownProperties) {
      const unknownProperties =
        localDevice.value.unknownProperties?.toLowerCase() || '';
      let scannedNumber = localDevice.value.serialNumber.toLowerCase();
      if (localDevice.value instanceof LoRaWanGateway) {
        scannedNumber = localDevice.value.eui.toLowerCase();
      }

      localDevice.value.detectionType = unknownProperties.includes(
        scannedNumber
      )
        ? detectionTypes.CodeScannen
        : detectionTypes.ManuelleErfassung;
    }
  }
);

useHardwareScanListener(scanResult, () => localDevice.value.deviceType);

watch(
  () => localDevice.value.productKey,
  (newValue) => {
    if (!newValue) return;
    const mappedDevice = articleNumberMapper.getMapping(newValue);
    if (mappedDevice?.customizedNumber) {
      localDevice.value.customizedNumber = mappedDevice.customizedNumber;
    }
  },
  { immediate: true }
);
</script>
